import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  OnChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss']
})
export class DateRangePickerComponent implements OnInit, OnDestroy, OnChanges {
  @Input() show = false;
  @Input() triggerElement?: HTMLElement;
  @Input() singleDateMode = false; // MonoSelector mode
  @Input() inline = false; // Inline mode (not positioned, no backdrop)
  @Input() compactMode = false; // Compact mode for smaller UI (used inside cards)
  @Input() dateSelectionMode: 'all' | 'future' | 'past' = 'all'; // Controls whether user can select past, future, or both dates
  @Input() includeToday: boolean = true; // Controls whether today's date should be selectable
  /**
   * Controls whether dates after today are selectable.
   * Default: false — future dates are disabled for all consumers.
   * Pass [allowFutureDates]="true" to enable future date selection.
   */
  @Input() allowFutureDates: boolean = false;
  @Output() showChange = new EventEmitter<boolean>();
  @Output() dateRangeSelected = new EventEmitter<DateRange>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('dropdownContainer', { static: false }) dropdownContainer?: ElementRef;

  // Calendar state
  currentMonth: Date = new Date();
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  hoveredDate: Date | null = null;

  // Positioning
  dropdownPosition: { top: string; left: string; right?: string } = { top: '0px', left: '0px' };
  isPositioned = false; // Track if dropdown has been positioned

  // Calendar helpers
  weekDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];

  monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  calendarDays: (Date | null)[][] = [];

  // Dropdown states for month and year selection
  showMonthDropdown = false;
  showYearDropdown = false;

  // Year range for year dropdown (10 years)
  yearRanges: number[] = [];

  ngOnInit(): void {
    this.generateCalendar();
    this.generateYearRange();
    document.addEventListener('click', this.captureClickHandler, true);
  }

  ngOnDestroy(): void {
    this.removeScrollListener();
    document.removeEventListener('click', this.captureClickHandler, true);
  }

  private scrollListenerAdded = false;
  private boundScrollHandler = this.onContainerScroll.bind(this);
  private captureClickHandler = (event: Event) => this.onDocumentClick(event as MouseEvent);

  private addScrollListener(): void {
    if (this.scrollListenerAdded) return;

    // Listen on content-area (the actual scrollable element)
    const contentArea = this.getContentAreaContainer();
    if (contentArea) {
      contentArea.addEventListener('scroll', this.boundScrollHandler, { passive: true });
      this.scrollListenerAdded = true;
    }

    // Also listen on main-content as fallback
    const container = this.getMainContentContainer();
    if (container && container !== contentArea) {
      container.addEventListener('scroll', this.boundScrollHandler, { passive: true });
    }
  }

  private removeScrollListener(): void {
    if (!this.scrollListenerAdded) return;

    const contentArea = this.getContentAreaContainer();
    if (contentArea) {
      contentArea.removeEventListener('scroll', this.boundScrollHandler);
    }

    const container = this.getMainContentContainer();
    if (container && container !== contentArea) {
      container.removeEventListener('scroll', this.boundScrollHandler);
    }

    this.scrollListenerAdded = false;
  }

  private onContainerScroll(): void {
    if (this.show && this.triggerElement) {
      this.positionDropdown();
    }
  }

  // Reference to the main content container (viewport)
  private mainContentContainer: HTMLElement | null = null;
  // Reference to the scrollable content area
  private contentAreaContainer: HTMLElement | null = null;

  /**
   * Listen for window resize and zoom events to reposition dropdown
   */
  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowChange(): void {
    if (this.show && this.triggerElement) {
      this.positionDropdown();
    }
  }

  /**
   * Get the main content container as the viewport reference
   */
  private getMainContentContainer(): HTMLElement | null {
    if (!this.mainContentContainer) {
      // Try to find .main-content container (app-layout viewport)
      this.mainContentContainer = document.querySelector('.main-content') as HTMLElement;
    }
    return this.mainContentContainer;
  }

  /**
   * Get the scrollable content area container
   */
  private getContentAreaContainer(): HTMLElement | null {
    if (!this.contentAreaContainer) {
      // Try to find .content-area container (the actual scrollable element)
      this.contentAreaContainer = document.querySelector('.content-area') as HTMLElement;
    }
    return this.contentAreaContainer;
  }

  /**
   * Get the viewport bounds (main-content container or fallback to window)
   */
  private getViewportBounds(): { top: number; left: number; right: number; bottom: number; width: number; height: number; scrollTop: number; scrollLeft: number } {
    const container = this.getMainContentContainer();

    if (container) {
      const rect = container.getBoundingClientRect();
      const contentArea = this.getContentAreaContainer();
      return {
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
        scrollTop: contentArea?.scrollTop || container.scrollTop,
        scrollLeft: contentArea?.scrollLeft || container.scrollLeft
      };
    }

    // Fallback to window viewport
    return {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
      scrollTop: window.scrollY,
      scrollLeft: window.scrollX
    };
  }

  /**
   * Generate year range (10 years centered around current year)
   */
  generateYearRange(): void {
    const currentYear = this.currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 10) * 10;
    this.yearRanges = [];
    for (let i = 0; i < 10; i++) {
      this.yearRanges.push(startYear + i);
    }
  }

  ngOnChanges(): void {
    if (this.show && this.triggerElement) {
      this.isPositioned = false; // Reset positioning flag
      // Reset cached container references to ensure fresh lookup
      this.mainContentContainer = null;
      this.contentAreaContainer = null;
      // Add scroll listener when dropdown opens
      this.addScrollListener();
      // Use requestAnimationFrame for smoother positioning
      requestAnimationFrame(() => {
        this.positionDropdown();
        this.isPositioned = true;
      });
    } else if (!this.show) {
      this.isPositioned = false; // Reset when hiding
      // Remove scroll listener when dropdown closes
      this.removeScrollListener();
    }
  }

  /**
   * Position the dropdown intelligently to stay within the main-content viewport
   */
  positionDropdown(): void {
    if (!this.triggerElement || !this.dropdownContainer) return;

    const triggerRect = this.triggerElement.getBoundingClientRect();
    const dropdownElement = this.dropdownContainer.nativeElement as HTMLElement;
    const dropdownRect = dropdownElement.getBoundingClientRect();

    // Get viewport bounds (main-content container or window)
    const viewport = this.getViewportBounds();

    // Minimum padding from viewport edges
    const edgePadding = 8;

    // Calculate initial position (below trigger) using fixed positioning
    let top = triggerRect.bottom + edgePadding;
    let left = triggerRect.left;

    // In compact mode, right-align the calendar with the trigger button
    if (this.compactMode) {
      left = triggerRect.right - dropdownRect.width;
    }

    // Check if dropdown fits on the right side within viewport
    if (!this.compactMode && left + dropdownRect.width > viewport.right - edgePadding) {
      // Align to the right edge of trigger
      left = triggerRect.right - dropdownRect.width;
    }

    // Ensure dropdown doesn't go off left edge of viewport
    if (left < viewport.left + edgePadding) {
      left = viewport.left + edgePadding;
    }

    // Ensure dropdown doesn't go off right edge of viewport
    if (left + dropdownRect.width > viewport.right - edgePadding) {
      left = viewport.right - dropdownRect.width - edgePadding;
    }

    // Check if dropdown fits below within viewport
    if (triggerRect.bottom + dropdownRect.height + edgePadding > viewport.bottom) {
      // Position above trigger if there's more space above
      const spaceAbove = triggerRect.top - viewport.top;
      const spaceBelow = viewport.bottom - triggerRect.bottom;

      if (spaceAbove > spaceBelow && spaceAbove >= dropdownRect.height + edgePadding) {
        // Position above
        top = triggerRect.top - dropdownRect.height - edgePadding;
      } else {
        // Not enough space above or below, constrain to viewport bottom
        top = viewport.bottom - dropdownRect.height - edgePadding;
      }
    }

    // Ensure dropdown doesn't go off top edge of viewport
    if (top < viewport.top + edgePadding) {
      top = viewport.top + edgePadding;
    }

    this.dropdownPosition = {
      top: `${top}px`,
      left: `${left}px`
    };
  }

  /**
   * Generate calendar grid for current month
   */
  generateCalendar(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();

    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Build calendar grid
    const weeks: (Date | null)[][] = [];
    let week: (Date | null)[] = [];

    // Fill initial empty days
    for (let i = 0; i < startingDayOfWeek; i++) {
      week.push(null);
    }

    // Fill month days
    for (let day = 1; day <= daysInMonth; day++) {
      week.push(new Date(year, month, day));

      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }

    // Fill remaining empty days
    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      weeks.push(week);
    }

    this.calendarDays = weeks;
  }

  /**
   * Navigate to previous month
   */
  previousMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.generateCalendar();
  }

  /**
   * Navigate to next month
   */
  nextMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.generateCalendar();
  }

  /**
   * Handle date selection
   */
  selectDate(date: Date | null): void {
    if (!date) return;

    // Don't allow selection of dates after today
    if (this.isDisabled(date)) {
      return;
    }

    // Reset time to midnight for comparison
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    if (this.singleDateMode) {
      // In single date mode, set both start and end to the same date
      this.selectedStartDate = selectedDate;
      this.selectedEndDate = selectedDate;
      // Auto-apply immediately in single date mode
      this.apply();
    } else {
      if (!this.selectedStartDate || (this.selectedStartDate && this.selectedEndDate)) {
        // Start new selection
        this.selectedStartDate = selectedDate;
        this.selectedEndDate = null;
      } else {
        // Complete range selection
        if (selectedDate < this.selectedStartDate) {
          this.selectedEndDate = this.selectedStartDate;
          this.selectedStartDate = selectedDate;
        } else {
          this.selectedEndDate = selectedDate;
        }

        // Auto-apply when end date is selected
        this.apply();
      }
    }
  }

  /**
   * Handle date hover for range preview
   */
  onDateHover(date: Date | null): void {
    if (this.selectedStartDate && !this.selectedEndDate && date) {
      this.hoveredDate = date;
    } else {
      this.hoveredDate = null;
    }
  }

  /**
   * Check if date is selected (start or end)
   */
  isSelected(date: Date | null): boolean {
    if (!date) return false;

    const dateTime = date.getTime();
    const startTime = this.selectedStartDate?.getTime();
    const endTime = this.selectedEndDate?.getTime();

    return dateTime === startTime || dateTime === endTime;
  }

  /**
   * Check if date is in selected range
   */
  isInRange(date: Date | null): boolean {
    if (!date || !this.selectedStartDate) return false;

    const dateTime = date.getTime();
    const startTime = this.selectedStartDate.getTime();

    if (this.selectedEndDate) {
      const endTime = this.selectedEndDate.getTime();
      return dateTime > startTime && dateTime < endTime;
    } else if (this.hoveredDate) {
      const hoverTime = this.hoveredDate.getTime();
      const minTime = Math.min(startTime, hoverTime);
      const maxTime = Math.max(startTime, hoverTime);
      return dateTime > minTime && dateTime < maxTime;
    }

    return false;
  }

  /**
   * Check if date is start of range
   */
  isRangeStart(date: Date | null): boolean {
    if (!date || !this.selectedStartDate) return false;
    return date.getTime() === this.selectedStartDate.getTime();
  }

  /**
   * Check if date is end of range
   */
  isRangeEnd(date: Date | null): boolean {
    if (!date) return false;

    if (this.selectedEndDate) {
      return date.getTime() === this.selectedEndDate.getTime();
    } else if (this.hoveredDate && this.selectedStartDate) {
      return date.getTime() === this.hoveredDate.getTime() &&
             this.hoveredDate.getTime() !== this.selectedStartDate.getTime();
    }

    return false;
  }

  /**
   * Check if date is today
   */
  isToday(date: Date | null): boolean {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  /**
   * Check if date is disabled based on allowFutureDates, dateSelectionMode, and includeToday settings.
   *
   * Priority order:
   * 1. allowFutureDates=false  → future dates always disabled (default)
   * 2. includeToday=false      → today disabled
   * 3. dateSelectionMode       → fine-grained past/future/all control
   */
  isDisabled(date: Date | null): boolean {
    if (!date) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    const isToday = checkDate.getTime() === today.getTime();
    const isFuture = checkDate > today;
    const isPast = checkDate < today;

    // Block all future dates unless explicitly allowed
    if (!this.allowFutureDates && isFuture) {
      return true;
    }

    // Handle includeToday setting
    if (isToday && !this.includeToday) {
      return true;
    }

    // Handle dateSelectionMode
    switch (this.dateSelectionMode) {
      case 'future':
        return isPast;
      case 'past':
        return isFuture;
      case 'all':
      default:
        return false;
    }
  }

  /**
   * Get current month and year display
   */
  getMonthYearDisplay(): string {
    return `${this.months[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  /**
   * Toggle month dropdown
   */
  toggleMonthDropdown(event: Event): void {
    event.stopPropagation();
    this.showMonthDropdown = !this.showMonthDropdown;
    this.showYearDropdown = false; // Close year dropdown if open
  }

  /**
   * Toggle year dropdown
   */
  toggleYearDropdown(event: Event): void {
    event.stopPropagation();
    this.showYearDropdown = !this.showYearDropdown;
    this.showMonthDropdown = false; // Close month dropdown if open
  }

  /**
   * Select month
   */
  selectMonth(monthIndex: number, event: Event): void {
    event.stopPropagation();
    this.currentMonth = new Date(this.currentMonth.getFullYear(), monthIndex, 1);
    this.generateCalendar();
    this.showMonthDropdown = false;
  }

  /**
   * Select year
   */
  selectYear(year: number, event: Event): void {
    event.stopPropagation();
    this.currentMonth = new Date(year, this.currentMonth.getMonth(), 1);
    this.generateCalendar();
    this.generateYearRange(); // Regenerate year range for new decade if needed
    this.showYearDropdown = false;
  }

  /**
   * Navigate to previous year range
   */
  previousYearRange(): void {
    const firstYear = this.yearRanges[0];
    const startYear = firstYear - 10;
    this.yearRanges = [];
    for (let i = 0; i < 10; i++) {
      this.yearRanges.push(startYear + i);
    }
  }

  /**
   * Navigate to next year range
   */
  nextYearRange(): void {
    const firstYear = this.yearRanges[0];
    const startYear = firstYear + 10;
    this.yearRanges = [];
    for (let i = 0; i < 10; i++) {
      this.yearRanges.push(startYear + i);
    }
  }

  /**
   * Apply selected date range
   */
  apply(): void {
    if (this.selectedStartDate) {
      const dateRange: DateRange = {
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate || this.selectedStartDate
      };
      this.dateRangeSelected.emit(dateRange);
      if (!this.inline) {
        this.close();
      }
    }
  }

  /**
   * Cancel and close
   */
  onCancel(): void {
    this.cancel.emit();
    if (!this.inline) {
      this.close();
    }
  }

  /**
   * Clear selection
   */
  clear(): void {
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.hoveredDate = null;
  }

  /**
   * Close dropdown
   */
  close(): void {
    this.show = false;
    this.showChange.emit(false);
  }

  /**
   * Close on outside click
   */
  onDocumentClick(event: MouseEvent): void {
    if (!this.show) return;
    if (this.inline) return;

    const target = event.target as HTMLElement;
    const clickedInside = this.dropdownContainer?.nativeElement.contains(target);
    const clickedTrigger = this.triggerElement?.contains(target);

    if (!clickedInside && !clickedTrigger) {
      this.close();
    }
  }

  /**
   * Prevent closing when clicking inside dropdown
   */
  onDropdownClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}
